'use client';

import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Fetch the user's notification preferences from Supabase
async function fetchNotifPrefs(userId: string, supabase: ReturnType<typeof createClient>) {
  const { data } = await supabase
    .from('notification_preferences')
    .select('mentions, coverage_gaps, time_off_approvals, urgent_items, announcements, room_messages')
    .eq('user_id', userId)
    .single();
  return data ?? {
    mentions: true,
    coverage_gaps: true,
    time_off_approvals: true,
    urgent_items: true,
    announcements: false,
    room_messages: false,
  };
}

export function usePushNotifications() {
  const { user } = useAuth();
  const supabase = createClient();
  const registered = useRef(false);

  useEffect(() => {
    if (!user || registered.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    async function registerPush() {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          if (!VAPID_PUBLIC_KEY) return;
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
        }

        const subJson = subscription.toJSON();
        if (!subJson.keys) return;

        await supabase.from('push_subscriptions').upsert(
          {
            user_id: user.id,
            endpoint: subscription.endpoint,
            p256dh: subJson.keys.p256dh,
            auth_key: subJson.keys.auth,
          },
          { onConflict: 'user_id,endpoint' }
        );

        registered.current = true;
      } catch {
        // Push registration failed silently
      }
    }

    registerPush();
  }, [user]);
}

export function useInAppNotifications() {
  const { user } = useAuth();
  const supabase = createClient();

  useEffect(() => {
    if (!user) return;

    let mentionChannel: ReturnType<typeof supabase.channel> | null = null;
    let urgentChannel: ReturnType<typeof supabase.channel> | null = null;

    async function setupChannels() {
      const prefs = await fetchNotifPrefs(user.id, supabase);

      // Mentions channel — only subscribe if user wants mention notifications
      if (prefs.mentions) {
        mentionChannel = supabase
          .channel(`mention-notifications-${Date.now()}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'message_mentions',
              filter: `mentioned_user_id=eq.${user.id}`,
            },
            async (payload) => {
              const mention = payload.new as { message_id: string; room_id: string };
              const { data: msg } = await supabase
                .from('room_messages')
                .select('sender_name, content, room_id')
                .eq('id', mention.message_id)
                .single();

              if (msg && 'Notification' in window && Notification.permission === 'granted') {
                new Notification(`@mention in ${msg.room_id}`, {
                  body: `${msg.sender_name}: ${msg.content.slice(0, 80)}`,
                  icon: '/favicon.ico',
                  tag: `mention-${mention.message_id}`,
                });
              }
            }
          )
          .subscribe();
      }

      // Urgent items channel — only subscribe if user wants urgent notifications
      if (prefs.urgent_items) {
        urgentChannel = supabase
          .channel(`urgent-needs-attention-${Date.now()}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'needs_attention_items',
            },
            (payload) => {
              const item = payload.new as { is_urgent: boolean; title: string; action_needed: string };
              if (item.is_urgent && 'Notification' in window && Notification.permission === 'granted') {
                new Notification('Urgent item needs attention', {
                  body: `${item.title}: ${item.action_needed?.slice(0, 80) ?? ''}`,
                  icon: '/favicon.ico',
                  tag: `urgent-${Date.now()}`,
                });
              }
            }
          )
          .subscribe();
      }
    }

    setupChannels();

    return () => {
      if (mentionChannel) supabase.removeChannel(mentionChannel);
      if (urgentChannel) supabase.removeChannel(urgentChannel);
    };
  }, [user]);
}
